/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {Events} from '@exc/shared';
import {messenger} from './Messenger';
import {NavConfig} from '@adobe/exc-app/sidebar';
import {SubOrgs} from '@adobe/exc-app/orgswitcher';

export interface ConfigProxy {
  appContainer?: string;
  customEnvLabel?: string;
  customButtons?: {
    id: string;
    label: string;
    scope: string;
  }[];
  coachMark?: {
    element: string;
    enabled: boolean;
  };
  customSearch?: {
    enabled: boolean;
    show: boolean;
  };
  digitalData?: any;
  favicon?: string;
  feedback?: {
    buttonLabel: string;
    enabled: boolean;
    subject?: string;
    type: 'external' | 'custom' | 'openFeedback';
    url: string;
  };
  helpCenter?: {
    featured: {
      description?: string;
      href: string;
      label?: string;
      path?: string;
    }[];
  };
  logoutUrl?: string;
  modal?: boolean;
  nps?: {
    enabled: boolean;
    sampling?: number;
    showToAdobeUsers: boolean;
  };
  preventDefaultCombos?: {
    altKey?: boolean;
    ctrlKey?: boolean;
    metaKey?: boolean;
    shiftKey?: boolean;
    key: string;
  }[];
  sidebar?: {
    config: NavConfig;
    collapsed: boolean;
    visible: boolean;
  };
  solution?: {
    icon: string;
    title: string;
    shortTitle: string;
  };
  spinner?: boolean;
  subOrgs?: SubOrgs;
  title?: string;
  unloadPromptMessage?: string;
  viewportTakeover?: boolean;
  workspaces?: {
    name: string;
    url: string;
  }[];
}

let config: ConfigProxy = {};

const configProxy = {
  clear: <T extends keyof ConfigProxy>(key?: T, cb?: string): void => {
    if (key) {
      delete config[key];
      cb && messenger.setCallback(cb, null);
      return;
    }
    config = {} as ConfigProxy;
  },
  config: (): Readonly<ConfigProxy> => config,
  get: <T extends keyof ConfigProxy>(key: T): ConfigProxy[T] => config[key],
  set: (updates: Record<string, any>): void => {
    messenger.send(Events.SHELL_SETTINGS, updates);
    Object.assign(config, updates);
  }
};

export function setupConfig<T extends keyof ConfigProxy>(obj: any, keys: string[], setCallback?: (key: string, value: any) => void, rollupObject?: T): void {
  keys.forEach(apiName => {
    Object.defineProperty(obj, apiName, {
      get: () => {
        if (rollupObject) {
          const object = configProxy.get(rollupObject);
          return object[apiName];
        }
        return configProxy.get(apiName as T);
      },
      set: value => {
        setCallback && setCallback(apiName, value);
        if (rollupObject) {
          configProxy.set({
            [rollupObject]: {[apiName]: value}
          });
          return;
        }
        configProxy.set({[apiName]: value});
      }
    });
  });
}

export function update<T extends keyof ConfigProxy>(key: T, value: any, callbackName: string, forceReset?: boolean): void {
  let newValue = Object.assign({}, value);
  let cb = newValue.callback;
  if (cb || forceReset) {
    delete newValue.callback;
    messenger.setCallback(callbackName, cb);
  }
  let current = configProxy.get(key) || {};
  configProxy.set({[key]: Object.assign(current, newValue)});
}

export default configProxy;
